import React, { useCallback, useState } from 'react'
import type { SeatData, SeatDisplayStatus, SeatRowData, VenueSectionData, SelectedSeat } from './SeatMap.types'

// ── Constantes SVG ────────────────────────────────────────────────────────────

const SEAT_SIZE = 22
const SEAT_GAP  = 4
const STEP      = SEAT_SIZE + SEAT_GAP   // 26px por celda
const ROW_LABEL_WIDTH = 28
const SECTION_PADDING = 8

const COLORS: Record<SeatDisplayStatus, string> = {
  AVAILABLE:     '#22c55e',
  SOLD:          '#ef4444',
  RESERVED_TEMP: '#f59e0b',
  SELECTED:      '#eab308',
  BLOCKED_ADMIN: '#374151',
  OFFLINE:       '#4b5563',
}

// Paleta de colores por sección (override del verde por defecto cuando seat está AVAILABLE)
const SECTION_COLORS: Record<string, string> = {
  green:  '#22c55e',
  blue:   '#3b82f6',
  purple: '#a855f7',
  orange: '#f97316',
  pink:   '#ec4899',
}

function resolveSeatColor(displayStatus: SeatDisplayStatus, sectionColor?: string): string {
  // Solo el estado AVAILABLE respeta el color de la sección.
  // Los demás estados conservan su semántica (rojo=ocupado, amarillo=seleccionado, etc.)
  if (displayStatus === 'AVAILABLE' && sectionColor && SECTION_COLORS[sectionColor]) {
    return SECTION_COLORS[sectionColor]
  }
  return COLORS[displayStatus]
}

const OPACITIES: Record<SeatDisplayStatus, number> = {
  AVAILABLE:     1,
  SOLD:          1,
  RESERVED_TEMP: 1,
  SELECTED:      1,
  BLOCKED_ADMIN: 1,
  OFFLINE:       0.35,
}

const CURSORS: Record<SeatDisplayStatus, string> = {
  AVAILABLE:     'pointer',
  SOLD:          'not-allowed',
  RESERVED_TEMP: 'not-allowed',
  SELECTED:      'pointer',
  BLOCKED_ADMIN: 'not-allowed',
  OFFLINE:       'default',
}

// ── Esquema de numeración por sección ────────────────────────────────────────
//
//  BUTACA LATERAL IZQ / PALCO IZQ  → impares  (1, 3, 5…)  right-align
//  BUTACA LATERAL DER / PALCO DER  → pares    (2, 4, 6…)  left-align
//  BUTACA CENTRAL / PALCO CENTRO   → 101+               right-align
//
//  "right-align" = asiento 1 siempre en columna DERECHA, escalón crece a izquierda
//  "left-align"  = asiento 1 siempre en columna IZQUIERDA, escalón crece a derecha

type Align = 'right' | 'left'

interface SectionScheme {
  align:       Align
  toPhysical:  (seqNumber: number) => number   // secuencial DB → número físico
  // Para secciones con escalón dual (CENTRAL): offset izquierdo variable por fila.
  // Si está ausente, right-align ancla al extremo derecho del grid (col maxSeatCount-1).
  leftOffset?: (rowSeats: number, maxSeats: number) => number
  // Columnas vacías reservadas en el extremo derecho (ej. PALCO FRONTAL IZQ fila DD).
  // Filas con n ≤ (maxSeatCount-rightPad) se anclan al borde reducido; filas más
  // largas usan right-align clásico para no dejar huecos.
  rightPad?:   number
  // Columnas vacías reservadas en el extremo izquierdo (ej. PALCO FRONTAL DER fila DD).
  // Filas con n + leftPad ≤ maxSeatCount arrancan en col leftPad; filas más largas
  // usan left-align clásico (col = i) para no sobresalir.
  leftPad?:    number
}

function getSectionScheme(section: VenueSectionData): SectionScheme {
  // Igual que resolveGridArea: buscar dirección en nombre Y subSection
  const full = `${section.name} ${section.subSection ?? ''}`.toUpperCase()

  if (full.includes('IZQ') || full.includes('LEFT') || full.includes('IZQUIERDA')) {
    // PALCO FRONTAL IZQ: fila DD tiene 2 columnas vacías en el extremo derecho del plano.
    // rightPad=2 corrige el ancla de right-align para las filas más cortas que CC.
    const rightPad = (full.includes('PALCO') && full.includes('FRONTAL')) ? 2 : 0
    return { align: 'right', toPhysical: n => 2 * n - 1, ...(rightPad ? { rightPad } : {}) }
  }
  if (full.includes('DER') || full.includes('RIGHT') || full.includes('DERECHA')) {
    // PALCO FRONTAL DER: fila DD tiene 2 columnas vacías en el extremo izquierdo del plano.
    // leftPad=2 desplaza las filas cortas hacia la derecha para reproducir el hueco izquierdo.
    const leftPad = (full.includes('PALCO') && full.includes('FRONTAL')) ? 2 : 0
    return { align: 'left', toPhysical: n => 2 * n, ...(leftPad ? { leftPad } : {}) }
  }
  // BUTACA CENTRAL: trapezoide con escalón doble — ambos lados crecen hacia atrás.
  // leftOffset = floor((maxSeats - n) * 2/5) reproduce exactamente el plano oficial.
  if (full.includes('BUTACA')) {
    return {
      align: 'right',
      toPhysical: n => n + 100,
      leftOffset: (n, maxN) => Math.floor((maxN - n) * 2 / 5),
    }
  }
  // PALCO CENTRAL: forma rectangular — right-align simple sin escalón.
  return {
    align: 'right',
    toPhysical: n => n + 100,
  }
}

// ── Mapeo de sección a grid-area del teatro ───────────────────────────────────

type GridArea =
  | 'but-izq' | 'but-ctr' | 'but-der'
  | 'pf-izq'  | 'pf-ctr'  | 'pf-der'
  | 'pp-izq'  | 'pp-der'

function resolveGridArea(section: VenueSectionData): GridArea | null {
  // Buscar en nombre Y subSection — la BD puede tener la dirección en cualquiera de los dos
  const name = section.name.toUpperCase().trim()
  const sub  = (section.subSection ?? '').toUpperCase().trim()
  // Texto combinado para detectar dirección sin importar en qué campo esté
  const full = `${name} ${sub}`

  const isIzq = full.includes('IZQ') || full.includes('LEFT')  || full.includes('IZQUIERDA')
  const isDer = full.includes('DER') || full.includes('RIGHT') || full.includes('DERECHA')
  const isCtr = full.includes('CENTRAL') || full.includes('CENTRO') || full.includes('CTR') || full.includes('CENTER')

  if (name.includes('BUTACA')) {
    if (name.includes('LATERAL') || name.includes('LAT')) {
      if (isIzq) return 'but-izq'
      if (isDer) return 'but-der'
    }
    // Sin "LATERAL": la dirección puede estar en subSection o en el nombre
    if (isIzq) return 'but-izq'
    if (isDer) return 'but-der'
    // CENTRAL: en nombre o subSection
    if (isCtr || name.includes('CENTRAL') || !sub) return 'but-ctr'
  }

  if (name.includes('PALCO')) {
    if (name.includes('FRONTAL') || name.includes('FRONT') || full.includes('FRONTAL')) {
      if (isIzq) return 'pf-izq'
      if (isCtr) return 'pf-ctr'
      if (isDer) return 'pf-der'
    }
    if (name.includes('POSTERIOR') || name.includes('POST') || full.includes('POSTERIOR')) {
      if (isIzq) return 'pp-izq'
      if (isDer) return 'pp-der'
    }
  }

  return null
}

// ── Nodo de asiento ───────────────────────────────────────────────────────────

interface SeatNodeProps {
  seat:          SeatData
  x:             number
  y:             number
  displayStatus: SeatDisplayStatus
  physicalNumber: number
  sectionColor?: string
  onClick:       () => void
}

const SeatNode = React.memo(function SeatNode({
  seat, x, y, displayStatus, physicalNumber, sectionColor, onClick,
}: SeatNodeProps) {
  const [hovered, setHovered] = useState(false)
  const isInteractive = displayStatus === 'AVAILABLE' || displayStatus === 'SELECTED'
  const baseOpacity   = OPACITIES[displayStatus]

  return (
    <g
      role="button"
      aria-label={`Asiento ${seat.label} (${physicalNumber}) — ${displayStatus}`}
      aria-pressed={displayStatus === 'SELECTED'}
      tabIndex={isInteractive ? 0 : -1}
      style={{ outline: 'none' }}
      opacity={baseOpacity}
      onClick={isInteractive ? onClick : undefined}
      onKeyDown={e => { if (isInteractive && (e.key === 'Enter' || e.key === ' ')) onClick() }}
      onMouseEnter={isInteractive ? () => setHovered(true)  : undefined}
      onMouseLeave={isInteractive ? () => setHovered(false) : undefined}
    >
      <rect
        x={x} y={y}
        width={SEAT_SIZE} height={SEAT_SIZE}
        rx={4}
        fill={resolveSeatColor(displayStatus, sectionColor)}
        opacity={hovered ? 0.8 : 1}
        style={{ cursor: CURSORS[displayStatus], transition: 'opacity 0.1s' }}
      />
      <text
        x={x + SEAT_SIZE / 2}
        y={y + SEAT_SIZE / 2 + 3}
        textAnchor="middle"
        fontSize={7}
        fontWeight="600"
        fill={displayStatus === 'AVAILABLE' ? '#14532d' : 'white'}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {physicalNumber}
      </text>
    </g>
  )
})

// ── Fila de asientos ──────────────────────────────────────────────────────────

interface SeatRowSvgProps {
  row:          SeatRowData
  y:            number
  maxSeatCount: number          // máx de toda la sección → define el ancho total
  align:        Align
  scheme:       SectionScheme
  sectionColor?: string
  selectedIds:  Set<number>
  conflictIds:  Set<number>
  onSeatClick:  (seat: SeatData) => void
}

const SeatRowSvg = React.memo(function SeatRowSvg({
  row, y, maxSeatCount, align, scheme, sectionColor, selectedIds, conflictIds, onSeatClick,
}: SeatRowSvgProps) {
  return (
    <g>
      {/* Etiqueta de fila */}
      <text
        x={ROW_LABEL_WIDTH - 4}
        y={y + SEAT_SIZE / 2 + 4}
        textAnchor="end"
        fontSize={11}
        fill="white"
        style={{ userSelect: 'none' }}
      >
        {row.rowLabel}
      </text>

      {/* Asientos */}
      {row.seats.map((seat, i) => {
        // Posición en el grid:
        //   right-align sin leftOffset → col = maxSeatCount-n+k-i (ancla al borde derecho efectivo)
        //   right-align con leftOffset → col = lOff+(n-1)-i       (trapezoide: col varía por fila)
        //   left-align                 → col = i                   (silla 1 siempre en col más izquierda)
        //
        //   rightPad: columnas vacías reservadas a la derecha (ej. PALCO FRONTAL IZQ fila DD).
        //   Filas con n ≤ effectiveMax se anclan al borde reducido;
        //   filas más largas usan right-align clásico (no dejan hueco a la izquierda).
        const n          = row.seats.length
        const rPad       = scheme.rightPad ?? 0
        const effectiveMax = maxSeatCount - rPad
        const lOff = scheme.leftOffset
          ? scheme.leftOffset(n, maxSeatCount)
          : n <= effectiveMax
            ? effectiveMax - n   // right-align dentro del borde reducido por rightPad
            : maxSeatCount - n   // right-align clásico para filas que superan effectiveMax

        const lPad = scheme.leftPad ?? 0
        const col = align === 'right'
          ? lOff + (n - 1 - i)
          : (n + lPad <= maxSeatCount ? lPad : 0) + i

        const x = ROW_LABEL_WIDTH + col * STEP

        const displayStatus: SeatDisplayStatus = conflictIds.has(seat.id)
          ? 'SOLD'
          : selectedIds.has(seat.id)
          ? 'SELECTED'
          : seat.status

        return (
          <SeatNode
            key={seat.id}
            seat={seat}
            x={x}
            y={y}
            displayStatus={displayStatus}
            physicalNumber={scheme.toPhysical(seat.seatNumber)}
            sectionColor={row.color ?? sectionColor}
            onClick={() => onSeatClick(seat)}
          />
        )
      })}
    </g>
  )
})

// ── Bloque de sección ─────────────────────────────────────────────────────────

interface SectionBlockProps {
  section:     VenueSectionData
  selectedIds: Set<number>
  conflictIds: Set<number>
  onSeatClick: (seat: SeatData) => void
}

function SectionBlock({ section, selectedIds, conflictIds, onSeatClick }: SectionBlockProps) {
  const scheme       = getSectionScheme(section)
  const maxSeatCount = Math.max(...section.rows.map(r => r.seats.length), 1)

  const svgWidth  = ROW_LABEL_WIDTH + maxSeatCount * STEP - SEAT_GAP + SECTION_PADDING
  const svgHeight = section.rows.length * STEP - SEAT_GAP + SECTION_PADDING * 2

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline gap-2">
        <span className="text-sm font-semibold text-secondary-120">
          {section.localityName ?? section.name}
        </span>
        {!section.localityName && section.subSection && (
          <span className="text-xs text-white/60">{section.subSection}</span>
        )}
      </div>
      <svg
        width={svgWidth}
        height={svgHeight}
        role="group"
        aria-label={`Sección ${section.name}${section.subSection ? ' ' + section.subSection : ''}`}
        style={{ display: 'block', overflow: 'visible' }}
      >
        {section.rows.map((row, ri) => (
          <SeatRowSvg
            key={row.rowLabel}
            row={row}
            y={SECTION_PADDING + ri * STEP}
            maxSeatCount={maxSeatCount}
            align={scheme.align}
            scheme={scheme}
            sectionColor={section.color}
            selectedIds={selectedIds}
            conflictIds={conflictIds}
            onSeatClick={onSeatClick}
          />
        ))}
      </svg>
    </div>
  )
}

// ── PumapungoLayout (exportado) ───────────────────────────────────────────────

export interface PumapungoLayoutProps {
  sections:      VenueSectionData[]
  selectedSeats: SelectedSeat[]
  onSeatClick:   (seat: SeatData) => void
  onConflictRef?: (flashFn: (seatId: number) => void) => void
}

/**
 * Layout oficial del Teatro Pumapungo (venue_id = 1).
 *
 * Rendering rules:
 *  - IZQ / IZQUIERDA → impares (1,3,5…), right-aligned → escalón crece a la izquierda
 *  - DER / DERECHA   → pares   (2,4,6…), left-aligned  → escalón crece a la derecha
 *  - CENTRAL / CENTRO → 101+,  right-aligned
 *
 * Los números mostrados son los FÍSICOS del teatro (impresos en los asientos),
 * aunque el DB almacena secuencial hasta corregir la migración.
 */
export function PumapungoLayout({
  sections,
  selectedSeats,
  onSeatClick,
  onConflictRef,
}: PumapungoLayoutProps) {
  const [conflictIds, setConflictIds] = useState<Set<number>>(new Set())

  const flashConflict = useCallback((seatId: number) => {
    setConflictIds(new Set([seatId]))
    setTimeout(() => setConflictIds(new Set()), 2000)
  }, [])

  React.useEffect(() => {
    onConflictRef?.(flashConflict)
  }, [onConflictRef, flashConflict])

  const handleSeatClick = useCallback(
    (seat: SeatData) => { setConflictIds(new Set()); onSeatClick(seat) },
    [onSeatClick],
  )

  const selectedIds = new Set(selectedSeats.map(s => s.seatId))

  // Agrupar secciones por área del grid.
  // Si varias secciones comparten área (ej. BUTACA CENTRAL VIP + Normal),
  // sus filas se fusionan en un único SectionBlock para evitar superposición visual.
  const byArea = new Map<GridArea, VenueSectionData[]>()
  const unpositioned: VenueSectionData[] = []

  for (const section of sections) {
    const area = resolveGridArea(section)
    if (area) {
      if (!byArea.has(area)) byArea.set(area, [])
      byArea.get(area)!.push(section)
    } else {
      unpositioned.push(section)
    }
  }

  // Fusiona múltiples secciones del mismo área en una sola (solo para renderizado).
  // Conserva el array original `sections` en el padre para la lógica de checkout.
  function mergeForDisplay(sectionsInArea: VenueSectionData[]): VenueSectionData {
    const first = sectionsInArea[0]!
    if (sectionsInArea.length === 1) return first

    // Debug: ver qué filas trae cada sección antes de fusionar
    console.warn(
      '[PumapungoLayout] merge →',
      sectionsInArea.map(s =>
        `"${s.name}${s.subSection ? ' ' + s.subSection : ''}" [${s.rows.map(r => r.rowLabel).join(',')}]`
      ),
    )

    // Dedup por rowLabel: si el backend repite filas, quedarse con la que tenga asientos reales.
    // El backend puede enviar una sección "sombra" OFFLINE + una sección real — la real gana.
    // IMPORTANTE: cada fila guarda el color de SU sección origen (no el de la sección merged).
    const rowMap = new Map<string, SeatRowData>()
    for (const section of sectionsInArea) {
      for (const row of section.rows) {
        const taggedRow: SeatRowData = { ...row, color: section.color }
        const existing = rowMap.get(row.rowLabel)
        if (!existing) {
          rowMap.set(row.rowLabel, taggedRow)
        } else {
          // Si la nueva fila tiene algún asiento no-OFFLINE, reemplaza a la existente
          const hasReal = row.seats.some(s => s.status !== 'OFFLINE')
          if (hasReal) rowMap.set(row.rowLabel, taggedRow)
        }
      }
    }
    const allRows = [...rowMap.values()]
      .sort((a, b) => a.rowLabel.localeCompare(b.rowLabel))

    // Concatenar nombres únicos de localidad (ej: "PLATEA BAJA CP + MEET&GREET CP")
    // cuando varias secciones del mismo bloque tienen distinto precio/localidad.
    const uniqueLocNames: string[] = []
    for (const s of sectionsInArea) {
      if (s.localityName && !uniqueLocNames.includes(s.localityName)) {
        uniqueLocNames.push(s.localityName)
      }
    }
    const mergedLocalityName = uniqueLocNames.length > 0
      ? uniqueLocNames.join(' + ')
      : first.localityName

    return { ...first, rows: allRows, localityName: mergedLocalityName }
  }

  return (
    <div className="flex flex-col gap-4" data-testid="pumapungo-layout">
      {/* Escenario */}
      <div className="flex justify-center">
        <div
          className="rounded-md px-8 py-1.5 text-xs font-bold tracking-widest uppercase"
          style={{ background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.25em' }}
        >
          ▲ ESCENARIO
        </div>
      </div>

      {/* Grid del teatro — width:fit-content + margin:auto centra cuando cabe,
           y hace overflow solo a la derecha (scrolleable) cuando no cabe */}
      <div style={{ width: 'fit-content', margin: '0 auto' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateAreas: `
            "but-izq  but-ctr  but-der"
            "pf-izq   pf-ctr   pf-der"
            "pp-izq   .        pp-der"
          `,
          gridTemplateColumns: 'auto auto auto',
          gap: '20px 28px',
          alignItems: 'start',
        }}
      >
        {[...byArea.entries()].map(([area, sectionsInArea]) => (
          <div key={area} style={{ gridArea: area }}>
            <SectionBlock
              section={mergeForDisplay(sectionsInArea)}
              selectedIds={selectedIds}
              conflictIds={conflictIds}
              onSeatClick={handleSeatClick}
            />
          </div>
        ))}
      </div>
      </div>

      {/* Secciones no reconocidas → fallback vertical */}
      {unpositioned.length > 0 && (
        <div className="flex flex-wrap gap-6 justify-center mt-2">
          {unpositioned.map(section => (
            <SectionBlock
              key={section.id}
              section={section}
              selectedIds={selectedIds}
              conflictIds={conflictIds}
              onSeatClick={handleSeatClick}
            />
          ))}
        </div>
      )}
    </div>
  )
}
